// src/pages/Consultation/tabs/PricingTab.jsx - COMPLETE SIMPLIFIED VERSION
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  FiLock,
  FiUnlock,
  FiRefreshCw,
  FiPlus,
  FiAlertCircle,
  FiDollarSign,
  FiTrash2,
  FiPercent,
  FiTag,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import AdjustmentApprovalModal from "../../components/AdjustmentApprovalModal";
import {
  lockPricesThunk,
  unlockPricesThunk,
  calculatePricingThunk,
  removeAdjustmentThunk,
} from "../../../../store/thunks/consultationThunks";

const PricingTab = ({
  session,
  sessionPricing,
  cartItems = [],
  exchangeItems = [],
  onAddAdjustment,
  adminInfo,
}) => {
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  const isLocked = session?.priceLock?.isLocked || false;
  const canModify = session?.status === "ACTIVE" && !isLocked;

  // Get adjustments by status
  const activeAdjustments =
    session?.priceAdjustments?.filter((adj) => adj.status === "ACTIVE") || [];

  const pendingAdjustments =
    session?.priceAdjustments?.filter(
      (adj) => adj.status === "PENDING" && adj.requiresApproval
    ) || [];

  // Actions
  const handleLockToggle = async () => {
    if (
      !window.confirm(
        isLocked ? "Unlock pricing?" : "Lock pricing for 60 minutes?"
      )
    ) {
      return;
    }

    setProcessing(true);
    try {
      if (isLocked) {
        await dispatch(
          unlockPricesThunk({
            sessionId: session._id,
            adminCode: adminInfo.adminCode,
            adminName: adminInfo.adminName,
          })
        ).unwrap();
        toast.success("Pricing unlocked");
      } else {
        await dispatch(
          lockPricesThunk({
            sessionId: session._id,
            lockDurationMinutes: 60,
            adminCode: adminInfo.adminCode,
            adminName: adminInfo.adminName,
          })
        ).unwrap();
        toast.success("Pricing locked for 60 minutes");
      }
    } catch (error) {
      toast.error("Operation failed");
    }
    setProcessing(false);
  };

  const handleRecalculate = async () => {
    setProcessing(true);
    try {
      await dispatch(calculatePricingThunk(session._id)).unwrap();
      toast.success("Pricing updated");
    } catch (error) {
      toast.error("Failed to recalculate");
    }
    setProcessing(false);
  };

  const handleRemoveAdjustment = async (adjustmentId) => {
    if (!window.confirm("Remove this adjustment?")) return;

    try {
      await dispatch(
        removeAdjustmentThunk({
          sessionId: session._id,
          adjustmentId,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();
      toast.success("Adjustment removed");
      dispatch(calculatePricingThunk(session._id));
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  // Empty state
  if (!sessionPricing || !sessionPricing.items?.length) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <FiDollarSign className="mx-auto text-4xl text-gray-400 mb-3" />
        <p className="text-gray-600">Add items to see pricing</p>
      </div>
    );
  }

  const summary = sessionPricing.summary || {};

  return (
    <div className="space-y-4">
      {/* Control Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FiDollarSign className="text-primary" />
              Pricing
            </h3>

            {/* Status badges */}
            <div className="flex gap-2">
              {isLocked && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded">
                  LOCKED
                </span>
              )}
              {pendingAdjustments.length > 0 && (
                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                  {pendingAdjustments.length} PENDING
                </span>
              )}
              {activeAdjustments.length > 0 && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                  {activeAdjustments.length} ACTIVE
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {!isLocked && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleRecalculate}
                disabled={processing}
              >
                <FiRefreshCw
                  className={`mr-1 ${processing ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            )}

            <Button
              size="sm"
              variant={isLocked ? "danger" : "success"}
              onClick={handleLockToggle}
              disabled={processing}
            >
              {isLocked ? (
                <FiUnlock className="mr-1" />
              ) : (
                <FiLock className="mr-1" />
              )}
              {isLocked ? "Unlock" : "Lock"}
            </Button>

            {pendingAdjustments.length > 0 && (
              <Button
                size="sm"
                variant="warning"
                onClick={() => setShowApprovalModal(true)}
              >
                <FiAlertCircle className="mr-1" />
                Review {pendingAdjustments.length}
              </Button>
            )}

            {canModify && (
              <Button size="sm" onClick={onAddAdjustment}>
                <FiPlus className="mr-1" />
                Add Adjustment
              </Button>
            )}
          </div>
        </div>

        {/* Lock info */}
        {isLocked && session.priceLock.lockExpiresAt && (
          <div className="mt-3 p-2 bg-yellow-50 rounded text-xs">
            <FiClock className="inline mr-1 text-yellow-600" />
            <span className="text-yellow-700">
              Locked by {session.priceLock.lockedBy} • Expires:{" "}
              {new Date(session.priceLock.lockExpiresAt).toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Items & Adjustments */}
        <div className="lg:col-span-2 space-y-3">
          {/* Cart Items */}
          {sessionPricing.items?.map((item, idx) => (
            <ItemPricingCard key={idx} item={item} index={idx} />
          ))}

          {/* Exchange Items */}
          {sessionPricing.exchangeItems?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <FiTag className="text-green-600" />
                Exchange Items
              </h4>
              <div className="space-y-2">
                {sessionPricing.exchangeItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center p-2 bg-green-50 rounded"
                  >
                    <div>
                      <p className="text-sm font-medium">
                        {item.description || `Item ${idx + 1}`}
                      </p>
                      <p className="text-xs text-gray-600">
                        {item.metalDetails?.purity} • {item.netWeight}g
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-green-700">
                      ₹{(item.agreedValue || 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Adjustments */}
          {(activeAdjustments.length > 0 || pendingAdjustments.length > 0) && (
            <div className="bg-white rounded-lg shadow p-4">
              <h4 className="font-medium text-gray-700 mb-3">
                Price Adjustments
              </h4>
              <div className="space-y-2">
                {/* Active ones */}
                {activeAdjustments.map((adj) => (
                  <AdjustmentRow
                    key={adj.adjustmentId}
                    adjustment={adj}
                    onRemove={() => handleRemoveAdjustment(adj.adjustmentId)}
                    canRemove={canModify}
                  />
                ))}

                {/* Pending ones */}
                {pendingAdjustments.map((adj) => (
                  <AdjustmentRow
                    key={adj.adjustmentId}
                    adjustment={adj}
                    isPending={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Summary */}
        <div className="bg-white rounded-lg shadow p-4 h-fit sticky top-4">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FiDollarSign />
            Summary
          </h4>

          <div className="space-y-2 text-sm">
            <SummaryLine label="Subtotal" value={summary.subtotal} />

            {summary.totalDiscount > 0 && (
              <SummaryLine
                label="Item Discounts"
                value={-summary.totalDiscount}
                className="text-green-600"
              />
            )}

            {summary.totalAdjustments > 0 && (
              <SummaryLine
                label="Cart Adjustments"
                value={-summary.totalAdjustments}
                className="text-green-600"
              />
            )}

            <SummaryLine label="GST (3%)" value={summary.totalGST} />

            <div className="border-t pt-2">
              <SummaryLine
                label="Total"
                value={summary.grandTotal}
                className="font-semibold text-base"
              />
            </div>

            {summary.totalExchangeValue > 0 && (
              <>
                <SummaryLine
                  label="Exchange Credit"
                  value={-summary.totalExchangeValue}
                  className="text-green-600"
                />
                <div className="border-t pt-2 bg-primary/5 -mx-4 px-4 py-2 rounded">
                  <SummaryLine
                    label="Net Payable"
                    value={summary.netPayable}
                    className="font-bold text-lg text-primary"
                  />
                </div>
              </>
            )}
          </div>

          {/* Last updated */}
          {sessionPricing.calculatedAt && (
            <p className="text-xs text-gray-500 mt-4 text-center">
              <FiClock className="inline mr-1" />
              {new Date(sessionPricing.calculatedAt).toLocaleTimeString()}
            </p>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      <AdjustmentApprovalModal
        isOpen={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        pendingAdjustments={pendingAdjustments}
        sessionId={session._id}
        adminInfo={adminInfo}
      />
    </div>
  );
};

// Item Pricing Card
const ItemPricingCard = ({ item, index }) => {
  const pricing = item.finalPricing || item.originalPricing;
  const breakdown = pricing?.breakdown || {};
  const hasAdjustment = item.adjustments?.length > 0;

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="font-medium text-gray-800">
            {index + 1}. {item.productName}
          </h4>
          <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold">
            ₹{((breakdown.netPayable || 0) * item.quantity).toLocaleString()}
          </p>
          {hasAdjustment && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
              ADJUSTED
            </span>
          )}
        </div>
      </div>

      {/* Compact breakdown */}
      <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
        <div>Metal: ₹{(breakdown.metalValue || 0).toLocaleString()}</div>
        <div>Making: ₹{(breakdown.charges?.making || 0).toLocaleString()}</div>
        {breakdown.stoneCharges > 0 && (
          <div>Stones: ₹{breakdown.stoneCharges.toLocaleString()}</div>
        )}
      </div>

      {/* Item adjustments */}
      {item.adjustments?.map((adj, idx) => (
        <div
          key={idx}
          className="mt-2 p-1 bg-green-50 rounded text-xs flex items-center gap-1"
        >
          <FiCheckCircle className="text-green-600" />
          <span className="text-green-700">
            {adj.adjustment.type === "PERCENTAGE"
              ? `${adj.adjustment.value}% off`
              : `₹${adj.adjustment.value} off`}
          </span>
        </div>
      ))}
    </div>
  );
};

// Adjustment Row
const AdjustmentRow = ({ adjustment, onRemove, canRemove, isPending }) => {
  return (
    <div
      className={`flex items-center justify-between p-2 rounded ${
        isPending ? "bg-yellow-50" : "bg-green-50"
      }`}
    >
      <div className="flex items-center gap-2">
        {isPending ? (
          <FiClock className="text-yellow-600" />
        ) : (
          <FiPercent className="text-green-600" />
        )}
        <div>
          <p className="text-sm font-medium">
            {adjustment.reason}
            {isPending && (
              <span className="ml-2 text-xs text-yellow-600">(Pending)</span>
            )}
          </p>
          <p className="text-xs text-gray-600">
            {adjustment.adjustment.type === "PERCENTAGE"
              ? `${adjustment.adjustment.value}%`
              : adjustment.adjustment.type === "OVERRIDE_PRICE"
              ? `Override ₹${adjustment.adjustment.overridePrice}`
              : `₹${adjustment.adjustment.value}`}
            • {adjustment.scope}
          </p>
        </div>
      </div>

      {canRemove && !isPending && (
        <button
          onClick={onRemove}
          className="p-1 text-red-600 hover:bg-red-50 rounded"
        >
          <FiTrash2 size={14} />
        </button>
      )}
    </div>
  );
};

// Summary Line
const SummaryLine = ({ label, value, className = "" }) => (
  <div className={`flex justify-between items-center ${className}`}>
    <span>{label}:</span>
    <span>
      {value < 0 ? "-" : ""}₹{Math.abs(value || 0).toLocaleString()}
    </span>
  </div>
);

export default PricingTab;
